// Norms validator. Given a plot polygon and a list of objects, produces an
// array of violations. Purely functional — no DOM, no store dependency —
// so the caller re-runs it on every objects:change / plot:change event.
//
// Violation shape:
//   {
//     ruleId,
//     severity,      // "error" | "warning"
//     title,         // short label for the violation panel
//     message,       // full human-readable description
//     normRef,       // "СП 53.13330.2019, п. 6.7"
//     objectIds,     // [id] or [idA, idB] — objects implicated
//     distanceM,     // measured distance (≤ minDistanceM, or null for containment)
//     minDistanceM,  // required minimum (null for containment)
//   }

import {
  objectFootprintPolygon,
  distancePolygonsM,
  distancePolygonInsideToBoundaryM,
  pointInPolygonM,
} from "./geometry"
import { polygonAreaM2 } from "../floorplan/faces"

// Main entry point. disabledRuleIds is a Set<string>.
export function validate({ plot, objects, ruleSet, disabledRuleIds }) {
  if (!plot?.polygonM || plot.polygonM.length < 3) return []
  if (!ruleSet?.rules) return []
  if (!objects || objects.length === 0) return []

  const polygon = plot.polygonM
  const violations = []
  const disabled = disabledRuleIds ?? new Set()

  for (const rule of ruleSet.rules) {
    if (disabled.has(rule.id)) continue
    if (rule.kind === "containment") {
      checkContainment(rule, polygon, objects, violations)
    } else if (rule.kind === "object-to-boundary") {
      checkBoundary(rule, polygon, objects, violations)
    } else if (rule.kind === "pair-distance") {
      checkPairDistance(rule, objects, violations)
    } else if (rule.kind === "coverage-ratio") {
      checkCoverage(rule, polygon, objects, violations)
    }
  }

  return violations
}

// Plot built-up ratio (коэффициент застройки): the summed footprint area of all
// building-type objects over the plot area. A site-level (plot-wide) check, so
// it carries NO objectIds — it isn't any single object's fault — and the panel
// shows it as a standalone warning. Thresholds vary by local zoning (ПЗЗ), so
// this is a loose advisory with a disclaimer in the message.
function checkCoverage(rule, polygon, objects, out) {
  const plotArea = polygonAreaM2(polygon)
  if (!(plotArea > 0)) return
  const built = new Set(rule.buildTypes ?? [])
  let sum = 0
  for (const obj of objects) {
    if (!built.has(obj.type)) continue
    sum += polygonAreaM2(objectFootprintPolygon(obj))
  }
  const ratio = sum / plotArea
  if (ratio > rule.maxRatio) {
    const pct = Math.round(ratio * 100)
    out.push({
      ruleId: rule.id,
      severity: rule.severity,
      // The percentage lives in the title because the panel/tooltip render the
      // title + normRef (and a distance line) but not the message.
      title: `${rule.title} ≈ ${pct}%`,
      message: `${rule.message} Текущая застройка ≈ ${pct}%.`,
      normRef: rule.normRef,
      objectIds: [],
      distanceM: null,
      minDistanceM: null,
      ratio,
      maxRatio: rule.maxRatio,
    })
  }
}

function checkContainment(rule, polygon, objects, out) {
  const excluded = new Set(rule.excludeTypes ?? [])
  for (const obj of objects) {
    if (excluded.has(obj.type)) continue
    // Containment and boundary rules are designed to be orthogonal:
    // - anyCornerInside=true  → boundary rule applies (rect at least
    //   partly inside → informative "≥X m from boundary" message covers it)
    // - anyCornerInside=false → rect fully outside (or the rare case of
    //   rect large enough to contain the plot) → containment fires alone.
    // Without this gate, a rect with one wall coincident with the plot edge
    // would double-flag as both containment and boundary.
    const corners = objectFootprintPolygon(obj)
    if (corners.some((c) => pointInPolygonM(c, polygon))) continue
    out.push({
      ruleId: rule.id,
      severity: rule.severity,
      title: rule.title,
      message: rule.message,
      normRef: rule.normRef,
      objectIds: [obj.id],
      distanceM: null,
      minDistanceM: null,
    })
  }
}

function checkBoundary(rule, polygon, objects, out) {
  for (const obj of objects) {
    if (obj.type !== rule.sourceType) continue
    // Skip only objects fully outside the plot — containment rule handles
    // those (reporting them as both containment + boundary would be noisy).
    // Objects that touch or cross the boundary still belong to the boundary
    // rule so the user sees the informative "≥X m from boundary" message
    // instead of the generic containment one. "fully outside" = no corner
    // is inside the polygon.
    const corners = objectFootprintPolygon(obj)
    const anyInside = corners.some((c) => pointInPolygonM(c, polygon))
    if (!anyInside) continue
    const d = distancePolygonInsideToBoundaryM(corners, polygon)
    if (d < rule.minDistanceM) {
      out.push({
        ruleId: rule.id,
        severity: rule.severity,
        title: rule.title,
        message: rule.message,
        normRef: rule.normRef,
        objectIds: [obj.id],
        distanceM: d,
        minDistanceM: rule.minDistanceM,
      })
    }
  }
}

function checkPairDistance(rule, objects, out) {
  const a = rule.typeA, b = rule.typeB
  // Collect candidates by type, avoid N² loop over all objects.
  const listA = objects.filter((o) => o.type === a)
  const listB = objects.filter((o) => o.type === b)
  const seen = new Set()
  for (const oa of listA) {
    for (const ob of listB) {
      if (oa.id === ob.id) continue
      // Deduplicate when typeA === typeB (shouldn't happen with current
      // rule set, but cheap insurance).
      const key = oa.id < ob.id ? `${oa.id}|${ob.id}` : `${ob.id}|${oa.id}`
      if (seen.has(key)) continue
      seen.add(key)
      const d = distancePolygonsM(objectFootprintPolygon(oa), objectFootprintPolygon(ob))
      if (d < rule.minDistanceM) {
        out.push({
          ruleId: rule.id,
          severity: rule.severity,
          title: rule.title,
          message: rule.message,
          normRef: rule.normRef,
          objectIds: [oa.id, ob.id],
          distanceM: d,
          minDistanceM: rule.minDistanceM,
        })
      }
    }
  }
}

// Convenience for panels: groups violations by objectId and counts
// them (null when no objects are implicated — shouldn't happen).
export function violationsByObjectId(violations) {
  const map = new Map()
  for (const v of violations) {
    for (const id of v.objectIds) {
      if (!map.has(id)) map.set(id, [])
      map.get(id).push(v)
    }
  }
  return map
}
